import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";

const GREEN = "#1f5a2e";
const GREEN_DARK = "#17451f";
const LEAF = "#5a9e5f";
const GOLD = "#e0a526";

const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");

// API may return [] or { data: [] } or { categories: [] }
const extractList = (json) => {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.categories)) return json.categories;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.data?.categories)) return json.data.categories;
  if (Array.isArray(json?.items)) return json.items;
  return [];
};

/* The zero of "404": a ring with a small herb sprig growing inside it */
function SproutZero() {
  return (
    <svg viewBox="0 0 100 120" className="h-[0.8em] w-auto mx-[0.03em]" aria-hidden="true">
      <ellipse cx="50" cy="60" rx="38" ry="52" fill="none" stroke={GREEN} strokeWidth="13" />
      {/* stem */}
      <path d="M50 104 C49 84 51 64 50 34" fill="none" stroke={GREEN} strokeWidth="3.5" strokeLinecap="round" />
      {/* leaves */}
      <path d="M50 78 C66 75 75 63 73 50 C58 52 50 64 50 78Z" fill={LEAF} />
      <path d="M50 64 C35 61 26 50 28 38 C42 40 50 51 50 64Z" fill={LEAF} />
      <path d="M50 36 C41 27 42 16 50 9 C58 16 59 27 50 36Z" fill={GREEN} />
      {/* seed */}
      <circle cx="50" cy="104" r="4.5" fill={GOLD} />
    </svg>
  );
}

export default function NotFound() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const previous = document.title;
    document.title = "Page not found";
    return () => {
      document.title = previous;
    };
  }, []);

  /* optional: category shortcuts (hidden if the request fails) */
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${BASE_API}/category`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : []))
      .then((json) =>
        setCategories(
          extractList(json)
            .map((c) => ({ id: c.id ?? c._id, name: c.name ?? c.title }))
            .filter((c) => c.id && c.name)
            .slice(0, 4),
        ),
      )
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const search = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term) navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  return (
    <main className="w-full min-h-screen bg-[#f6f8f2] pt-[72px] flex items-center justify-center px-4">
      <div className="w-full max-w-[620px] text-center py-12 sm:py-16">
        {/* 404 */}
        <div
          className="flex items-center justify-center font-serif font-bold leading-none select-none text-[120px] sm:text-[190px]"
          style={{ color: GREEN }}
          aria-hidden="true"
        >
          <span>4</span>
          <SproutZero />
          <span>4</span>
        </div>

        <h1 className="mt-6 font-serif text-[26px] sm:text-[34px] leading-tight" style={{ color: GREEN }}>
          This page has wandered off
        </h1>
        <p className="sr-only">Error 404</p>
        <p className="mt-3 mx-auto max-w-[460px] text-[14px] sm:text-[16px] leading-relaxed text-[#5b6b5e]">
          The link may be broken, or the page may have moved. Let’s get you back to something good.
        </p>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-[46px] px-7 rounded-full text-white text-[14px] font-[600] transition-colors"
            style={{ backgroundColor: GREEN }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = GREEN_DARK)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = GREEN)}
          >
            Back to home <FaArrowRight className="text-[12px]" />
          </Link>
          <Link
            to="/shop"
            className="inline-flex items-center h-[46px] px-7 rounded-full border text-[14px] font-[600] bg-white hover:bg-[#eef5ea] transition-colors"
            style={{ borderColor: GREEN, color: GREEN }}
          >
            Shop products
          </Link>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 text-[13px] text-[#6b7a6d] underline underline-offset-4 hover:text-[#1f5a2e]"
        >
          Go back to the previous page
        </button>

        {/* Search */}
        {/* <form
          onSubmit={search}
          role="search"
          className="mt-10 mx-auto max-w-[460px] flex items-center gap-3 h-[48px] px-5 rounded-full bg-white border border-[#dfe6d3] focus-within:border-[#2f6b3a] transition-colors"
        >
          <CiSearch className="text-[22px] text-gray-600 shrink-0" aria-hidden="true" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search for what you were looking for"
            aria-label="Search products"
            className="flex-1 min-w-0 bg-transparent text-[14px] outline-none placeholder-gray-500"
          />
          <button type="submit" className="text-[13px] font-[600] shrink-0" style={{ color: GREEN }}>
            Search
          </button>
        </form> */}

        {/* Category shortcuts */}
        {categories.length > 0 && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2.5">
            <span className="text-[13px] text-[#6b7a6d] mr-1">Browse:</span>
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/product-category/${c.id}`}
                className="px-4 py-1.5 rounded-full bg-white border border-[#dfe6d3] text-[13px] hover:border-[#2f6b3a] hover:text-[#1f5a2e] transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
